import { FC, useMemo } from "react";
import { Paper, Skeleton, Stack, Tooltip, Typography } from "@mui/material";
import { formatCompactNumber } from "../../../../../../../common/utils";
import { ChartNoDataOverlay } from "../../../../../../../components/charts/components/ChartNoDataOverlay";
import { GraphBarChartLegend } from "../../../../../../../components/charts/components/GraphBarChartLegend";
import { MeasurableValueDTO } from "../../../../../../../datasources/api-client/data-contracts";
import { getTransportTypeIcon } from "../../AggregateScenario/data";

export type ModalShiftDeltaChartItem = {
	haulageId: number;
	haulageLabel: string;
	baseTkm: MeasurableValueDTO;
	compTkm: MeasurableValueDTO;
	co2Delta: MeasurableValueDTO;
};

type Props = {
	data?: ModalShiftDeltaChartItem[];
	title?: string;
	increaseLabel?: string;
	decreaseLabel?: string;
	measure?: string;
	height?: number;
	intervalsPerSide?: number;
	formatTick?: (value: number) => string;
	isLoading?: boolean;
};

type Axis = {
	min: number;
	max: number;
	range: number;
	ticks: number[];
};

const axisWidth = 88;
const barHeight = 12;
const axisPaddingRatio = 0.025;
const comparisonTolerance = 0.000001;

const regularTickHeight = 6;
const zeroTickHeight = 8;
const boundaryTickHeight = 10;

const regularLabelTop = 9;
const boundaryLabelTop = 27;

// Removes floating point errors
const normalizeNumber = (value: number): number => {
	return Number(value.toPrecision(12));
};

// Checks if two values are equivalent
const areEqual = (first: number, second: number): boolean => {
	return Math.abs(first - second) < comparisonTolerance;
};

// Removes duplicate values
const getUniqueValues = (values: number[]): number[] => {
	return values.filter((value, index, items) => items.findIndex((item) => areEqual(item, value)) === index);
};

// Builds the axis configuration
const buildAxis = (values: number[], intervalsPerSide: number): Axis => {
	const minValue = Math.min(...values, 0);
	const maxValue = Math.max(...values, 0);

	const negativeExtent = Math.abs(minValue);
	const positiveExtent = Math.abs(maxValue);

	// Stable axis when all values are zero
	if (negativeExtent === 0 && positiveExtent === 0) {
		return {
			min: -1,
			max: 1,
			range: 2,
			ticks: [-0.75, -0.5, -0.25, 0, 0.25, 0.5, 0.75],
		};
	}

	let min: number;
	let max: number;

	// All values are positive
	if (negativeExtent === 0) {
		min = 0;
		max = normalizeNumber(positiveExtent * (1 + axisPaddingRatio));
	}
	// All values are negative
	else if (positiveExtent === 0) {
		min = normalizeNumber(-(negativeExtent * (1 + axisPaddingRatio)));
		max = 0;
	}
	// Values exist on both sides
	else {
		const dataSpan = negativeExtent + positiveExtent;
		const boundaryPadding = (dataSpan * axisPaddingRatio) / 2;

		min = normalizeNumber(-(negativeExtent + boundaryPadding));

		max = normalizeNumber(positiveExtent + boundaryPadding);
	}

	const range = max - min;

	// Use the longest side for one shared scale
	const longestExtent = Math.max(negativeExtent, positiveExtent);

	const safeIntervals = Math.max(Math.floor(intervalsPerSide), 1);

	const tickStep = longestExtent / safeIntervals;

	const negativeTicks: number[] = [];
	const positiveTicks: number[] = [];

	for (let index = 1; index <= safeIntervals; index += 1) {
		const negativeTick = normalizeNumber(-(tickStep * index));

		const positiveTick = normalizeNumber(tickStep * index);

		// Keep regular negative ticks inside the boundaries
		if (negativeTick > min && !areEqual(negativeTick, minValue)) {
			negativeTicks.push(negativeTick);
		}

		// Keep regular positive ticks inside the boundaries
		if (positiveTick < max && !areEqual(positiveTick, maxValue)) {
			positiveTicks.push(positiveTick);
		}
	}

	const ticks = getUniqueValues([...negativeTicks.reverse(), 0, ...positiveTicks]);

	return {
		min,
		max,
		range,
		ticks,
	};
};

const ModalShiftDeltaBarChart: FC<Props> = ({
	data = [],
	title,
	increaseLabel = "Increase",
	decreaseLabel = "Decrease",
	measure = "Delta kgCO₂e wtw",
	height = 360,
	intervalsPerSide = 4,
	formatTick = (value) => formatCompactNumber(value, 0, 1),
	isLoading = false,
}) => {
	const values = useMemo(() => data.map((item) => item.co2Delta?.value ?? 0), [data]);

	const hasNoData = values.length === 0 || values.every((value) => value === 0);

	const leftAxisWidth = hasNoData ? 0 : axisWidth;

	const axis = useMemo(() => buildAxis(values, intervalsPerSide), [values, intervalsPerSide]);

	// Converts a value into its horizontal position
	const getPosition = (value: number): number => {
		if (axis.range <= 0) {
			return 50;
		}

		const position = ((value - axis.min) / axis.range) * 100;

		return Math.min(Math.max(position, 0), 100);
	};

	const zeroPosition = getPosition(0);

	if (isLoading) {
		return <Skeleton variant="rounded" height={300} width="100%" />;
	}

	return (
		<Paper elevation={0} sx={{ p: 2 }}>
			<Stack spacing={2} width="100%" minHeight={height}>
				{title && (
					<Typography variant="h6" fontWeight={500}>
						{title}
					</Typography>
				)}

				<Stack direction="row" spacing={1} flex={1}>
					{/* Y axis labels */}
					{!hasNoData && (
						<Stack width={leftAxisWidth} minWidth={leftAxisWidth} justifyContent="space-around" gap={2}>
							{data.map((item) => (
								<Stack key={item.haulageId} alignItems="center" spacing={0.5}>
									{getTransportTypeIcon(item.haulageId)}

									<Typography variant="caption" color="text.secondary" noWrap>
										{item.haulageLabel}
									</Typography>
								</Stack>
							))}
						</Stack>
					)}

					{/* Chart area */}
					<Stack flex={1} position="relative" gap={2}>
						{/* Regular vertical grid lines */}
						{axis.ticks.map((value) => {
							const left = getPosition(value);

							const isZero = areEqual(value, 0);

							return (
								<Stack
									key={`grid-${value}`}
									position="absolute"
									left={`${left}%`}
									top={0}
									bottom={0}
									width="1px"
									bgcolor="divider"
									sx={{
										transform: "translateX(-50%)",
										opacity: isZero ? 1 : 0.5,
									}}
								/>
							);
						})}

						{/* Left graph boundary */}
						<Stack
							position="absolute"
							left={0}
							top={0}
							bottom={0}
							width="1px"
							bgcolor="divider"
							sx={{ opacity: 0.8 }}
						/>

						{/* Right graph boundary */}
						<Stack
							position="absolute"
							right={0}
							top={0}
							bottom={0}
							width="1px"
							bgcolor="divider"
							sx={{ opacity: 0.8 }}
						/>

						{/* Empty state */}
						{hasNoData && <ChartNoDataOverlay />}

						{/* Bars */}
						{!hasNoData && (
							<Stack justifyContent="space-around" height="100%" position="relative">
								{data.map((item) => {
									const value = item.co2Delta?.value ?? 0;

									const valuePosition = getPosition(value);

									const isPositive = value > 0;

									const isNegative = value < 0;

									const left = isNegative ? valuePosition : zeroPosition;

									const width = Math.abs(valuePosition - zeroPosition);

									return (
										<Tooltip
											key={item.haulageId}
											title={`${formatTick(value)} ${item.co2Delta?.measure ?? ""}`}
											arrow
										>
											<Stack position="relative" height={barHeight}>
												{/* Negative bar */}
												{isNegative && (
													<Stack
														position="absolute"
														left={`${left}%`}
														width={`${width}%`}
														height="100%"
														borderRadius="4px 0 0 4px"
														bgcolor="success.main"
													/>
												)}

												{/* Positive bar */}
												{isPositive && (
													<Stack
														position="absolute"
														left={`${zeroPosition}%`}
														width={`${width}%`}
														height="100%"
														borderRadius="0 4px 4px 0"
														bgcolor="error.main"
													/>
												)}
											</Stack>
										</Tooltip>
									);
								})}
							</Stack>
						)}
					</Stack>
				</Stack>

				{/* X axis */}
				<Stack direction="row" spacing={1}>
					{!hasNoData && <Stack width={leftAxisWidth} minWidth={leftAxisWidth} />}

					<Stack flex={1} position="relative" height={52}>
						{/* Horizontal axis line */}
						<Stack position="absolute" top={0} left={0} right={0} height="1px" bgcolor="divider" />

						{/* Regular ticks */}
						{axis.ticks.map((value) => {
							const left = getPosition(value);

							const isZero = areEqual(value, 0);

							const isLeftBoundary = areEqual(value, axis.min);

							const isRightBoundary = areEqual(value, axis.max);

							// Boundaries are rendered separately
							if (isLeftBoundary || isRightBoundary) {
								return null;
							}

							return (
								<Stack
									key={`tick-${value}`}
									position="absolute"
									left={`${left}%`}
									top={0}
									alignItems="center"
									sx={{
										transform: "translateX(-50%)",
									}}
								>
									<Stack
										width="1px"
										height={isZero ? zeroTickHeight : regularTickHeight}
										bgcolor={isZero ? "text.primary" : "text.secondary"}
									/>

									<Typography
										variant="caption"
										color="text.secondary"
										whiteSpace="nowrap"
										position="absolute"
										top={regularLabelTop}
									>
										{formatTick(value)}
									</Typography>
								</Stack>
							);
						})}

						{/* Left boundary */}
						<Stack position="absolute" left={0} top={0} alignItems="flex-start">
							<Stack width="1px" height={boundaryTickHeight} bgcolor="text.primary" />

							<Typography
								variant="caption"
								color="text.primary"
								fontWeight={600}
								whiteSpace="nowrap"
								position="absolute"
								top={boundaryLabelTop}
								left={0}
							>
								{formatTick(axis.min)}
							</Typography>
						</Stack>

						{/* Right boundary */}
						<Stack position="absolute" right={0} top={0} alignItems="flex-end">
							<Stack width="1px" height={boundaryTickHeight} bgcolor="text.primary" />

							<Typography
								variant="caption"
								color="text.primary"
								fontWeight={600}
								whiteSpace="nowrap"
								position="absolute"
								top={boundaryLabelTop}
								right={0}
							>
								{formatTick(axis.max)}
							</Typography>
						</Stack>
					</Stack>
				</Stack>

				{/* Legend */}
				{!hasNoData && (
					<GraphBarChartLegend title={measure} primaryLable={increaseLabel} secondaryLable={decreaseLabel} />
				)}
			</Stack>
		</Paper>
	);
};

export default ModalShiftDeltaBarChart;
